/**
 * Turn an API error into something a shopper can read in their own language.
 *
 * The API answers in English — "Insufficient stock for this variant" — and the
 * storefront was printing that string straight onto an Azerbaijani page. The
 * translation belongs here rather than in the backend: the `detail` field is a
 * machine interface that tests and other clients match on, and localising it at
 * the source would mean every consumer had to parse a moving target.
 *
 * Matching is on a stable substring, so a message that grows a suffix (an SKU,
 * a count) still resolves. Anything unrecognised falls through to a generic
 * line rather than leaking English at the shopper.
 */

/** Backend `detail` substring → i18n key. Order matters: first match wins. */
const ERROR_KEYS = [
  ["Invalid credentials", "error.bad_credentials"],
  ["Account disabled", "error.account_disabled"],
  ["Invalid refresh token", "error.session_expired"],
  ["Activation link", "error.activation_invalid"],
  ["Reset link", "error.reset_invalid"],
  ["Email already registered", "error.email_taken"],
  ["not activated", "error.not_activated"],
  ["Insufficient stock", "error.out_of_stock"],
  ["no longer available", "error.variant_gone"],
  ["Variant not found", "error.variant_gone"],
  ["Invalid product/variant pair", "error.variant_gone"],
  ["Cart not found", "error.cart_gone"],
  ["Cart access denied", "error.cart_gone"],
  ["Cart is not active", "error.cart_gone"],
  ["Cart is empty", "error.cart_empty"],
  ["Item not found", "error.item_gone"],
  ["Product not found", "error.product_gone"],
  ["Phone number or email is required", "error.contact_required"],
  ["Email is required", "error.email_required"],
  ["Phone number is invalid", "error.phone_invalid"],
  ["Terms acceptance is required", "error.terms_required"],
  ["Shipping address is incomplete", "error.address_incomplete"],
  ["Verification code", "error.code_invalid"],
  ["Invalid or expired code", "error.code_invalid"],
  ["Please wait", "error.rate_limited"],
  ["Insufficient points", "error.points_short"],
  ["Quantity must be positive", "error.quantity_positive"],
  ["not currently available to reserve", "error.reserve_unavailable"],
  ["Unable to hold stock", "error.reserve_failed"],
  ["No companion variants selected", "error.no_companions"],
  ["Too many attempts", "error.rate_limited"],
];

const FALLBACKS = {
  "error.bad_credentials": "E-poçt və ya parol yanlışdır.",
  "error.account_disabled": "Bu hesab bağlanıb. Konsyerjlə əlaqə saxlayın.",
  "error.session_expired": "Sessiyanın vaxtı bitib. Yenidən daxil olun.",
  "error.activation_invalid": "Aktivləşdirmə linki yanlışdır və ya vaxtı bitib.",
  "error.reset_invalid": "Parol linki yanlışdır və ya vaxtı bitib.",
  "error.email_taken": "Bu e-mail ünvanı ilə artıq hesab var. Daxil olun və ya parolu bərpa edin.",
  "error.not_activated": "Hesab hələ aktivləşdirilməyib. E-mailinizdəki linkdən parol təyin edin.",
  "error.out_of_stock": "Bu ölçüdə kifayət qədər stok yoxdur.",
  "error.variant_gone": "Seçilmiş variant artıq mövcud deyil.",
  "error.cart_gone": "Səbət tapılmadı və ya artıq tamamlanıb.",
  "error.cart_empty": "Səbət boşdur.",
  "error.item_gone": "Bu məhsul səbətdə tapılmadı.",
  "error.product_gone": "Məhsul tapılmadı.",
  "error.contact_required": "Sifarişi tamamlamaq üçün telefon nömrəsi lazımdır.",
  "error.email_required": "Sifarişi tamamlamaq üçün e-poçt ünvanı lazımdır.",
  "error.phone_invalid": "Telefon nömrəsini düzgün formatda yazın.",
  "error.terms_required": "Davam etmək üçün şərtləri qəbul edin.",
  "error.address_incomplete": "Çatdırılma ünvanı natamamdır — şəhər və ünvan sətrini doldurun.",
  "error.code_invalid": "Daxil etdiyiniz kod yanlışdır və ya vaxtı bitib. Yenidən yoxlayın.",
  "error.points_short": "Bal balansınız kifayət etmir.",
  "error.quantity_positive": "Say sıfırdan böyük olmalıdır.",
  "error.reserve_unavailable": "Bu parça hazırda saxlanıla bilməz.",
  "error.reserve_failed": "Bu parça üçün stok saxlanıla bilmədi.",
  "error.no_companions": "Heç bir tamamlayıcı parça seçilməyib.",
  "error.rate_limited": "Çox sayda cəhd oldu. Bir az sonra yenidən yoxlayın.",
  "error.generic": "Əməliyyat tamamlanmadı. Yenidən cəhd edin.",
};

/**
 * @param error  an axios error (or anything with response.data.detail)
 * @param t      the `t(key, fallback)` from useI18n()
 */
export function apiErrorMessage(error, t) {
  const detail = error?.response?.data?.detail;
  // FastAPI validation errors arrive as a list of objects, never as prose.
  const text = typeof detail === "string" ? detail : "";
  const match = ERROR_KEYS.find(([needle]) => text.includes(needle));
  const key = match ? match[1] : "error.generic";
  return t(key, FALLBACKS[key]);
}
